import { z } from "zod"

export const ApiResponseSchema = z.object({
    statusCode: z.number(),
    message: z.string().optional(),
    data: z.any().optional(),
    errorCode: z.string().nullable().optional(),
})

export type ApiResponse<T> = {
    statusCode: number
    message?: string
    data?: T
    errorCode?: string | null
    retryable?: boolean
}

export type TApiResponseWithRequiredData<T> = Omit<ApiResponse<T>, 'data'> & { data: NonNullable<ApiResponse<T>['data']> }

export interface PaginationMeta {
    totalCount: number
    pageNumber: number
    pageSize: number
    totalPages: number
}

export interface PaginatedData<T> {
    items: T[]
    totalCount: number
    pageNumber: number
    pageSize: number
}

export type PaginatedResponse<T> = ApiResponse<PaginatedData<T>>

export const ErrorResponseSchema = z.object({
    statusCode: z.number(),
    message: z.string().optional(),
    errorCode: z.string().optional(),
})

export type ErrorResponse = z.infer<typeof ErrorResponseSchema>

// Common error codes (to be expanded based on API documentation)
export enum ErrorCode {
    INVALID_CREDENTIALS = "INVALID_CREDENTIALS",
    ACCOUNT_LOCKED = "ACCOUNT_LOCKED",
    ACCOUNT_DISABLED = "ACCOUNT_DISABLED",
    VALIDATION_ERROR = "VALIDATION_ERROR",
    SERVER_ERROR = "SERVER_ERROR",
    UNAUTHORIZED = "UNAUTHORIZED",
    FORBIDDEN = "FORBIDDEN",
    NOT_FOUND = "NOT_FOUND",
}

// Error messages for common error codes
export const ErrorMessages: Record<string, string> = {
    [ErrorCode.INVALID_CREDENTIALS]: "Invalid username or password. Please try again.",
    [ErrorCode.ACCOUNT_LOCKED]: "Your account has been locked. Please contact support.",
    [ErrorCode.ACCOUNT_DISABLED]: "Your account has been disabled. Please contact support.",
    [ErrorCode.VALIDATION_ERROR]: "Please check your input and try again.",
    [ErrorCode.SERVER_ERROR]: "An unexpected error occurred. Please try again later.",
    [ErrorCode.UNAUTHORIZED]: "You are not authorized to perform this action.",
    [ErrorCode.FORBIDDEN]: "You do not have permission to access this resource.",
    [ErrorCode.NOT_FOUND]: "The requested resource was not found.",
}

// Get a user-friendly error message based on error code
export function getErrorMessage(errorCode?: string, fallbackMessage?: string): string {
    if (!errorCode) return fallbackMessage || "An unexpected error occurred. Please try again."
    return ErrorMessages[errorCode] || fallbackMessage || "An unexpected error occurred. Please try again."
}
